import React, { useState, useRef, useEffect, useContext } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import CartAddedPopup from "../components/CartAddedPopup";
import FAQSection from "../components/FAQSection";
import BrikPhotoGallery from "../components/BrikPhotoGallery";
import { CartContext } from "../context/CartContext";
import {
  organizationJsonLd,
  websiteJsonLd,
  } from "../seo/structuredData";
import "./Home.css";
import confetti from "canvas-confetti";
import funPic from "../assets/funpic.JPG";
import brikwallet from "../assets/new-version/4.png";
import boxes from "../assets/boxes1.webp";
import feat5 from "../assets/new-version/5.png";
import feat6 from "../assets/new-version/6.png";
import feat7 from "../assets/new-version/7.png";
import feat8 from "../assets/new-version/8.png";
import feat9 from "../assets/new-version/9.png";
import heroPhoto from "../assets/newest-version/Full Front - Everything extended.jpg";

const HOMEPAGE_FEATURES = [
  {
    src: feat5,
    alt: "The Metal Brik wallet holding cash, with keys attached to the integrated keyring",
  },
  {
    src: feat6,
    alt: "ID card in the quick-access front slot of the Metal Brik wallet",
  },
  {
    src: feat7,
    alt: "Credit cards in the RFID-protected middle compartment of the Metal Brik wallet",
  },
  {
    src: feat8,
    alt: "Cash held under the elastic band on the back of the Metal Brik wallet",
  },
  {
    src: feat9,
    alt: "Keys attached to the integrated key holder of the Metal Brik wallet",
  },
];

// Real metal-Brik buyer reviews; the full set of 20 lives on /metal-wallet.
const HOMEPAGE_REVIEWS = [
  {
    text: "I like having my keys, cards, cash, and tracker all in one place instead of checking three different pockets.",
  },
  {
    text: "I used to do the phone-wallet-keys pocket check every morning. Now it's basically just phone and Brik.",
  },
  {
    text: "This is one of those products that seems unusual until you use it, and then a regular wallet feels incomplete.",
  },
];

const futura = "'Futura', sans-serif";

const METAL_WALLET = {
  id: 100,
  name: "Metal Wallet",
  description:
    "Machined from solid metal. Holds cards, cash, ID, keys, and an in-built tracking card.",
  price: 69.99,
  link: "/metal-wallet",
  images: ["/models/open.glb"],
};

const Home = () => {
  const { addToCart } = useContext(CartContext);
  const [preorderNotice, setPreorderNotice] = useState("");
  const [stickyVisible, setStickyVisible] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [platform, setPlatform] = useState(null); // "Apple" | "Android"
  const [addedVariant, setAddedVariant] = useState(METAL_WALLET);

  const heroRef = useRef(null);
  const newsletterRef = useRef(null);
  const heroViewportHeight = "calc(100svh - 96px)";

  // Show sticky add-to-cart bar once user scrolls past hero
  useEffect(() => {
    if (!heroRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setStickyVisible(!entry.isIntersecting),
      { threshold: 0, rootMargin: "0px 0px -200px 0px" }
    );
    observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  const fireConfetti = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    confetti({
      particleCount: 90,
      spread: 70,
      origin: {
        x: (rect.left + rect.width / 2) / window.innerWidth,
        y: (rect.top + rect.height / 2) / window.innerHeight,
      },
    });
  };

  const handlePreorderAdd = (e) => {
    if (!platform) {
      setPreorderNotice("Please select Apple or Android first.");
      setTimeout(() => setPreorderNotice(""), 2500);
      heroRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    const variant = {
      ...METAL_WALLET,
      id: platform === "Apple" ? "100-apple" : "100-android",
      name: `Metal Wallet (${platform})`,
      platform,
    };
    addToCart({ ...variant, quantity: 1 });
    setAddedVariant(variant);
    setPreorderNotice("Added to cart!");
    setTimeout(() => setPreorderNotice(""), 1500);
    setPopupOpen(true);
    if (typeof window.gtag === "function") {
      window.gtag("event", "add_to_cart", {
        currency: "USD",
        value: 69.99,
        items: [
          {
            item_id: `metal-wallet-${platform.toLowerCase()}`,
            item_name: variant.name,
            price: 69.99,
            quantity: 1,
          },
        ],
      });
    }
    fireConfetti(e);
  };

  // Newsletter state
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterMessage, setNewsletterMessage] = useState("");
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleNewsletterSignup();
  };

  const handleNewsletterSignup = async () => {
    if (!newsletterEmail) {
      setNewsletterMessage("Please enter an email address.");
      return;
    }
    if (!emailRegex.test(newsletterEmail)) {
      setNewsletterMessage("Please enter a valid email address.");
      return;
    }
    setNewsletterMessage("");
    const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
    const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY;
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/email_list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_KEY,
          Authorization: `Bearer ${SUPABASE_KEY}`,
          Prefer: "return=representation",
        },
        body: JSON.stringify({ email: newsletterEmail }),
      });
      if (response.ok) {
        setNewsletterMessage("Thank you for signing up!");
        setNewsletterEmail("");
        if (typeof window.gtag === "function") {
          window.gtag("event", "sign_up", { method: "newsletter" });
        }
        if (newsletterRef.current) {
          const rect = newsletterRef.current.getBoundingClientRect();
          confetti({
            particleCount: 100,
            spread: 70,
            origin: {
              x: (rect.left + rect.width / 2) / window.innerWidth,
              y: (rect.top + rect.height / 2) / window.innerHeight,
            },
          });
        }
        setTimeout(() => setNewsletterMessage(""), 3000);
      } else {
        setNewsletterMessage(
          "There was an issue signing you up. Please try again later."
        );
      }
    } catch (error) {
      setNewsletterMessage("There was an error. Please try again later.");
    }
  };

  return (
    <div className="home" style={{ background: "#0c0c0c" }}>
      <SEO
        title="The Brik Wallet: Cards, ID, Keys & Tracker in One"
        description="The Brik Wallet keeps cards, ID, cash, keys, and a rechargeable tracking card in one compact metal wallet. $69.99, in stock, arrives in 5-7 days."
        path="/"
        keywords="the brik wallet, metal wallet with key holder, wallet with built-in tracker, metal wallet, minimalist metal wallet, wallet that holds keys, keychain wallet, edc wallet, slim wallet, metal cardholder"
        jsonLd={[organizationJsonLd, websiteJsonLd]}
      />
      <Header />

      {/* Top status strip */}
      <div
        style={{
          background: "#ff7d1a",
          color: "white",
          padding: "8px 18px",
          textAlign: "center",
          fontWeight: 700,
          fontSize: "13px",
          letterSpacing: "0.5px",
          fontFamily: futura,
          textTransform: "uppercase",
        }}
      >
        In stock now · arrives in 5-7 days · free US shipping
      </div>

      {/* ===== HERO: Metal Wallet ===== */}
      <section
        ref={heroRef}
        style={{
          background:
            "radial-gradient(ellipse at top, #2a2a2a 0%, #0c0c0c 70%)",
          color: "white",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
          alignItems: "stretch",
          gap: "0",
          minHeight: heroViewportHeight,
        }}
      >
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            height: "100%",
            minHeight: heroViewportHeight,
          }}
        >
          <BrikPhotoGallery fill autoPlayMs={4000}>
            <a
              href="/metal-wallet#3d"
              style={{
                marginTop: "4px",
                padding: "11px 22px",
                background: "rgba(0,0,0,0.35)",
                color: "#ff7d1a",
                border: "2px solid #ff7d1a",
                borderRadius: "999px",
                fontWeight: 700,
                fontSize: "13px",
                letterSpacing: "0.5px",
                fontFamily: futura,
                textTransform: "uppercase",
                textDecoration: "none",
                backdropFilter: "blur(2px)",
              }}
            >
              🕹️ Wanna play with the 3D models of the Brik? →
            </a>
          </BrikPhotoGallery>
        </div>

        <div
          style={{
            padding: "40px 48px",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            maxWidth: "560px",
            justifySelf: "center",
            boxSizing: "border-box",
          }}
        >
          <span
            style={{
              alignSelf: "flex-start",
              background: "#ff7d1a",
              color: "white",
              padding: "5px 12px",
              borderRadius: "20px",
              fontSize: "11px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "1.5px",
              fontFamily: futura,
            }}
          >
            New · In stock
          </span>

          <h1
            style={{
              fontFamily: futura,
              fontSize: "clamp(32px, 4.2vw, 52px)",
              lineHeight: 1.08,
              letterSpacing: "-1px",
              margin: 0,
            }}
          >
            The Metal Brik: One Wallet for Cards, ID, Keys, and Tracker
          </h1>

          <p
            style={{
              fontFamily: futura,
              fontSize: "18px",
              lineHeight: 1.5,
              color: "#cfcfcf",
              margin: 0,
            }}
          >
            The Brik is a metal wallet that keeps your cards, ID, cash, keys,
            and a rechargeable tracking card together. Machined from solid
            anodized aluminum. Built to outlast the original.
          </p>

          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: "14px",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                color: "rgba(255,255,255,0.45)",
                fontSize: "30px",
                fontWeight: 600,
                fontFamily: futura,
                letterSpacing: "-1px",
                textDecoration: "line-through",
              }}
            >
              $79.99
            </span>
            <span
              style={{
                color: "#7fffa8",
                fontSize: "48px",
                fontWeight: 700,
                fontFamily: futura,
                letterSpacing: "-1.5px",
              }}
            >
              $69.99
            </span>
          </div>

          <p
            style={{
              fontFamily: futura,
              fontSize: "14px",
              color: "#7fffa8",
              margin: 0,
              fontWeight: 600,
            }}
          >
            ★★★★★ Rated 4.8/5 by metal Brik buyers.
          </p>

          <div>
            <p
              style={{
                fontFamily: futura,
                fontSize: "13px",
                fontWeight: 700,
                letterSpacing: "0.5px",
                color: "#fff",
                margin: "0 0 4px",
                textTransform: "uppercase",
              }}
            >
              Which phone do you use?
            </p>
            <p
              style={{
                fontFamily: futura,
                fontSize: "12px",
                color: "#aaa",
                margin: "0 0 10px",
              }}
            >
              So your Brik ships with the right tracking card.
            </p>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              {["Apple", "Android"].map((p) => {
                const selected = platform === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPlatform(p)}
                    style={{
                      flex: "1 1 140px",
                      padding: "14px 20px",
                      borderRadius: "10px",
                      border: selected ? "2px solid #ff7d1a" : "2px solid #555",
                      background: selected ? "rgba(255,125,26,0.15)" : "transparent",
                      color: selected ? "#ff7d1a" : "#ddd",
                      fontWeight: 700,
                      fontSize: "15px",
                      cursor: "pointer",
                      letterSpacing: "0.5px",
                      fontFamily: futura,
                      transition: "all 0.15s ease",
                    }}
                  >
                    {selected ? "✓ " : ""}
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <button
              onClick={handlePreorderAdd}
              style={{
                flex: "1 1 220px",
                padding: "18px 28px",
                background: "#ff7d1a",
                color: "white",
                border: "none",
                borderRadius: "10px",
                fontWeight: 700,
                fontSize: "16px",
                cursor: "pointer",
                letterSpacing: "1px",
                fontFamily: futura,
                textTransform: "uppercase",
                boxShadow: "0 6px 20px rgba(255,125,26,0.4)",
                transition: "transform 0.1s ease",
              }}
              onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
              onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              Add to cart · $69.99
            </button>
            <button
              onClick={() => (window.location.href = "/metal-wallet")}
              style={{
                padding: "16px 24px",
                background: "transparent",
                color: "white",
                border: "2px solid white",
                borderRadius: "10px",
                fontWeight: 700,
                fontSize: "14px",
                cursor: "pointer",
                letterSpacing: "1px",
                fontFamily: futura,
                textTransform: "uppercase",
              }}
            >
              See details
            </button>
          </div>

          <div
            style={{
              display: "flex",
              gap: "20px",
              flexWrap: "wrap",
              fontFamily: futura,
              fontSize: "13px",
              color: "#bbb",
            }}
          >
            <span>✓ Secure checkout by Stripe</span>
            <span>✓ 30-day exchange guarantee</span>
            <span>✓ Free US shipping, no minimum</span>
            <span>✓ Ships next day, arrives in 5-7 days</span>
          </div>

          {preorderNotice && (
            <p
              style={{
                margin: 0,
                color: "#7fffa8",
                fontWeight: 600,
                fontFamily: futura,
              }}
            >
              {preorderNotice}
            </p>
          )}
        </div>
      </section>

      {/* ===== Feature images (labels already on the images) ===== */}
      <section
        style={{
          background: "#f7f7f7",
          padding: "60px 24px",
          color: "#111",
        }}
      >
        <h2
          style={{
            fontFamily: futura,
            fontSize: "clamp(24px, 2.5vw, 32px)",
            margin: "0 0 28px",
            textAlign: "center",
            letterSpacing: "-0.5px",
          }}
        >
          Every essential, organized.
        </h2>
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "16px",
          }}
        >
          {HOMEPAGE_FEATURES.map((feature, i) => (
            <div
              key={i}
              style={{
                background: "#989898",
                borderRadius: "12px",
                overflow: "hidden",
                aspectRatio: "3 / 2",
                flex: "1 1 200px",
                maxWidth: "220px",
                minWidth: "180px",
              }}
            >
              <img
                src={feature.src}
                alt={feature.alt}
                loading="lazy"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </div>
          ))}
        </div>
      </section>

      {/* ===== Reviews (3 cards) ===== */}
      <section
        style={{
          background: "white",
          padding: "70px 24px",
        }}
      >
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <h2
            style={{
              fontFamily: futura,
              fontSize: "clamp(28px, 3vw, 38px)",
              margin: "0 0 8px",
              letterSpacing: "-0.5px",
              textAlign: "center",
            }}
          >
            Rated 4.8 out of 5 by metal Brik buyers.
          </h2>
          <p
            style={{
              fontFamily: futura,
              fontSize: "16px",
              color: "#666",
              margin: "0 0 36px",
              textAlign: "center",
            }}
          >
            900+ people bought the original. Here's what metal Brik owners say.
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "20px",
              marginBottom: "32px",
            }}
          >
            {HOMEPAGE_REVIEWS.map((r) => (
              <div
                key={r.text}
                style={{
                  background: "#f7f7f7",
                  borderRadius: "12px",
                  padding: "26px 26px 22px",
                  flex: "1 1 260px",
                  maxWidth: "340px",
                  minWidth: "260px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    color: "#f5b315",
                    fontSize: "16px",
                    marginBottom: "10px",
                    letterSpacing: "2px",
                  }}
                  aria-label="5 out of 5 stars"
                >
                  ★★★★★
                </div>
                <p
                  style={{
                    fontFamily: futura,
                    fontSize: "15px",
                    lineHeight: 1.55,
                    color: "#222",
                    margin: "0 0 18px",
                    fontStyle: "italic",
                    flex: 1,
                  }}
                >
                  &ldquo;{r.text}&rdquo;
                </p>
                <p
                  style={{
                    fontFamily: futura,
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#888",
                    margin: 0,
                    letterSpacing: "0.3px",
                  }}
                >
                  Verified buyer
                </p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center" }}>
            <a
              href="/metal-wallet"
              style={{
                display: "inline-block",
                padding: "14px 28px",
                background: "#111",
                color: "white",
                borderRadius: "8px",
                fontWeight: 700,
                fontSize: "14px",
                letterSpacing: "1px",
                fontFamily: futura,
                textTransform: "uppercase",
                textDecoration: "none",
              }}
            >
              Read more reviews →
            </a>
          </div>
        </div>
      </section>

      {/* ===== From the guide library ===== */}
      <section style={{ background: "#f7f7f7", padding: "60px 24px" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <h2
            style={{
              fontFamily: futura,
              fontSize: "clamp(24px, 2.5vw, 32px)",
              margin: "0 0 8px",
              textAlign: "center",
              letterSpacing: "-0.5px",
              color: "#111",
            }}
          >
            From the guide library
          </h2>
          <p
            style={{
              fontFamily: futura,
              color: "#666",
              fontSize: "15px",
              textAlign: "center",
              margin: "0 0 28px",
            }}
          >
            Honest answers people are searching for right now.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
              gap: "12px",
            }}
          >
            {[
              {
                href: "/guides/best-wallet-for-people-who-lose-everything",
                label: "The best wallet for people who lose things",
              },
              {
                href: "/guides/birthday-gifts-for-husband",
                label: "Birthday gift ideas for your husband",
              },
              {
                href: "/guides/military-graduation-gift",
                label: "Military graduation gifts for him",
              },
              {
                href: "/guides/secret-santa-gifts-for-men",
                label: "Good Secret Santa gifts for men",
              },
              {
                href: "/guides/gym-bag-essentials-men",
                label: "Gym bag essentials for men",
              },
              {
                href: "/guides",
                label: "Browse all guides",
              },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                style={{
                  display: "block",
                  background: "white",
                  border: "1px solid #e6e6e6",
                  borderRadius: "10px",
                  padding: "16px 18px",
                  textDecoration: "none",
                  fontFamily: futura,
                  fontSize: "14.5px",
                  fontWeight: 600,
                  color: "#111",
                }}
              >
                {l.label} →
              </a>
            ))}
          </div>
        </div>
      </section>

      <main>
        <section className="business-section">
          <div className="business-image">
            <img loading="lazy" src={boxes} alt="The Brik packaging and shipping boxes" />
          </div>
          <div className="business-content">
            <h2>We get down to business.</h2>
            <p><strong>Free Shipping on Every Order</strong></p>
            <p><strong>Our Guarantee</strong></p>
            <p>
              We guarantee your satisfaction by enforcing a{" "}
              <a href="/exchange-and-delivery">exchange policy</a>.
            </p>
            <p><strong>Something Wrong?</strong></p>
            <p>
              If we haven't corrected a problem before you notice it, please
              reach out to us at:
              <a href="mailto:asray@the-brik.com" style={{ marginLeft: "5px" }}>
                asray@the-brik.com
              </a>
            </p>
            <div className="business-buttons">
              <button
                className="refund-button"
                onClick={() => (window.location.href = "/exchange-and-delivery")}
              >
                Exchange Policy
              </button>
              <button
                className="contact-button"
                onClick={() =>
                  document
                    .getElementById("contact-us")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                Contact Us
              </button>
            </div>
          </div>
        </section>

        <section className="about-section">
          <div className="about-content">
            <h2>Our Story</h2>
            <p>
              The Brik started with a simple idea:{" "}
              <strong>carry less, do more</strong>. We set out to design the
              ultimate <strong>pocket-sized</strong> organizer, blending{" "}
              <strong>functionality, durability, and style</strong> into one
              sleek tool.
            </p>
            <p>
              Built for{" "}
              <strong>students, professionals, and everyday adventurers</strong>
              , The Brik ensures you never fumble for essentials again. Whether
              it's your <strong>cards, keys, or multitools</strong>, it's all
              in one place, always secure, always accessible.
            </p>
            <button
              className="learn-more"
              onClick={() => (window.location.href = "/about")}
            >
              Learn More
            </button>
          </div>
          <div className="about-image">
            <img loading="lazy" src={funPic} alt="The Brik founders and product story" />
            <div className="newsletter" ref={newsletterRef}>
              <h3>Stay in the Loop</h3>
              <p>Sign up for exclusive updates and deals.</p>
              <div className="newsletter-input">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={newsletterEmail}
                  onKeyPress={handleKeyPress}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                />
                <button
                  className="subscribe-button"
                  onClick={handleNewsletterSignup}
                >
                  Sign Up
                </button>
              </div>
              {newsletterMessage && (
                <p className="newsletter-message">{newsletterMessage}</p>
              )}
            </div>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <div style={{ marginTop: "60px" }}>
          <FAQSection background="#f7f7f7" />
        </div>

        {/* ===== Stay in touch / contact ===== */}
        <section
          style={{
            background: "white",
            padding: "60px 24px",
            textAlign: "center",
          }}
        >
          <div style={{ maxWidth: "640px", margin: "0 auto" }}>
            <h2
              style={{
                fontFamily: futura,
                fontSize: "clamp(24px, 2.5vw, 32px)",
                margin: "0 0 12px",
                letterSpacing: "-0.5px",
                color: "#111",
              }}
            >
              Questions? Reach out.
            </h2>
            <p
              style={{
                fontFamily: futura,
                color: "#555",
                fontSize: "15px",
                lineHeight: 1.6,
                margin: "0 0 20px",
              }}
            >
              Email Asray directly at{" "}
              <a
                href="mailto:asray@the-brik.com"
                style={{ color: "#ff7d1a", fontWeight: 600 }}
              >
                asray@the-brik.com
              </a>{" "}
              for anything about your order, shipping, or the product.
            </p>
            <p
              style={{
                fontFamily: futura,
                color: "#555",
                fontSize: "15px",
                lineHeight: 1.6,
                margin: "0 0 20px",
              }}
            >
              Want to follow the build process? We post updates as we ship the
              metal version. Follow along below.
            </p>
            <div
              style={{
                display: "flex",
                gap: "14px",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <a
                href="https://www.instagram.com/the.brik/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: "10px 22px",
                  background: "#111",
                  color: "white",
                  borderRadius: "999px",
                  textDecoration: "none",
                  fontFamily: futura,
                  fontWeight: 700,
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}
              >
                Instagram
              </a>
              <a
                href="https://www.tiktok.com/@thebrik"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: "10px 22px",
                  background: "#111",
                  color: "white",
                  borderRadius: "999px",
                  textDecoration: "none",
                  fontFamily: futura,
                  fontWeight: 700,
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}
              >
                TikTok
              </a>
            </div>
          </div>
        </section>

        {/* ===== Internal nav strip ===== */}
        <section
          style={{
            background: "white",
            padding: "40px 24px",
            borderTop: "1px solid #eee",
            borderBottom: "1px solid #eee",
          }}
        >
          <div
            style={{
              maxWidth: "1000px",
              margin: "0 auto",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "32px",
              fontFamily: futura,
            }}
          >
            {[
              { href: "/metal-wallet", label: "Metal Wallet" },
              { href: "/shop", label: "Shop all" },
              { href: "/groomsmen-gifts", label: "Groomsmen gifts" },
              { href: "/gifts-for-college-students", label: "Gifts for college kids" },
              { href: "/wallet-for-college-students", label: "For college students" },
              { href: "/wallet-for-professionals", label: "For professionals" },
              { href: "/brik-vs-ridge", label: "Brik vs Ridge" },
              { href: "/wallet-with-built-in-tracker", label: "Built-in tracker" },
              { href: "/edc-wallet", label: "EDC wallet" },
              { href: "/about", label: "Our story" },
              { href: "/track", label: "Track an order" },
              { href: "/exchange-and-delivery", label: "Returns & shipping" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                style={{
                  fontSize: "14px",
                  color: "#333",
                  textDecoration: "none",
                  fontWeight: 600,
                  letterSpacing: "0.3px",
                }}
              >
                {link.label}
              </a>
            ))}
          </div>
        </section>

        {/* ===== What came before (plastic) ===== */}
        <section className="products" style={{ marginTop: "60px" }}>
          <h2>What came before</h2>
          <div className="product-container">
            <div
              className="product-card"
              onClick={() => (window.location.href = "/wallet")}
              style={{ position: "relative" }}
            >
              <span
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  background: "#b71c1c",
                  color: "white",
                  padding: "10px 18px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  zIndex: 2,
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                }}
              >
                No longer for sale
              </span>
              <img
                loading="lazy"
                src={brikwallet}
                style={{ borderRadius: "16px", opacity: 0.75 }}
                alt="The original plastic Brik wallet (discontinued)"
              />
              <h3>Wallet (plastic)</h3>
              <p style={{ color: "#888" }}>Discontinued</p>
            </div>
          </div>
        </section>
      </main>

      {/* ===== Sticky add-to-cart bar (with attached closed-view widget) ===== */}
      <div
        aria-hidden={!stickyVisible}
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999,
          transform: stickyVisible ? "translateY(0)" : "translateY(320px)",
          transition: "transform 0.35s ease",
          pointerEvents: stickyVisible ? "auto" : "none",
        }}
      >
        {/* Closed-view widget sits directly on top of the bar, no gap */}
        <div
          className="home-sticky-widget"
          style={{
            position: "absolute",
            left: "20px",
            bottom: "100%",
            background: "#1a1a1a",
            border: "1px solid #2a2a2a",
            borderBottom: "none",
            borderRadius: "16px 16px 0 0",
            padding: "12px",
            boxShadow: "0 -6px 20px rgba(0,0,0,0.35)",
          }}
        >
          <div
            style={{
              width: "200px",
              height: "150px",
              borderRadius: "10px",
              overflow: "hidden",
              background: "#0c0c0c",
            }}
          >
            <img
              loading="lazy"
              src={heroPhoto}
              alt="The Metal Brik wallet, holding cards and keys"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </div>
        </div>

        <div
          className="home-sticky-bar"
          style={{
            background: "#0c0c0c",
            color: "white",
            padding: "12px 16px",
            boxShadow: "0 -4px 20px rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            fontFamily: futura,
          }}
        >
          <div className="home-sticky-bar-info" style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: "14px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                The Metal Brik
              </div>
              <div style={{ fontSize: "12px", color: "#bbb" }}>
                <span style={{ color: "#777", fontWeight: 600, textDecoration: "line-through", marginRight: "5px" }}>$79.99</span>
                <span style={{ color: "#7fffa8", fontWeight: 700 }}>$69.99</span>{" "}
                · in stock
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            <div style={{ display: "flex", gap: "6px" }}>
              {["Apple", "Android"].map((p) => {
                const selected = platform === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPlatform(p)}
                    style={{
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: selected ? "2px solid #ff7d1a" : "2px solid #555",
                      background: selected ? "rgba(255,125,26,0.15)" : "transparent",
                      color: selected ? "#ff7d1a" : "#ddd",
                      fontWeight: 700,
                      fontSize: "12px",
                      cursor: "pointer",
                      fontFamily: futura,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selected ? "✓ " : ""}
                    {p}
                  </button>
                );
              })}
            </div>
            <button
              onClick={handlePreorderAdd}
              style={{
                background: "#ff7d1a",
                color: "white",
                border: "none",
                padding: "12px 22px",
                borderRadius: "8px",
                fontWeight: 700,
                fontSize: "14px",
                cursor: "pointer",
                letterSpacing: "0.5px",
                fontFamily: futura,
                textTransform: "uppercase",
                flexShrink: 0,
                boxShadow: "0 4px 14px rgba(255,125,26,0.35)",
              }}
            >
              Add to cart
            </button>
          </div>
        </div>
      </div>

      <Footer />
      {/* Spacer AFTER footer matching just the sticky bar height */}
      <div
        aria-hidden="true"
        style={{ height: stickyVisible ? "80px" : "0", transition: "height 0.3s ease" }}
      />

      <CartAddedPopup
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        productId={addedVariant.id}
        productName={addedVariant.name}
      />
    </div>
  );
};

export default Home;
